import { useEffect, useState } from "react";
import { X, UserPlus, UserMinus, Crown, User } from "lucide-react";
import { useRag } from "../RagContext";
import "./BandoMembersModal.css";

export default function BandoMembersModal({ open, onClose }) {
    const {
        currentBandoId,
        bandi,
        members,
        invitableUsers,
        loading,
        loadMembers,
        loadInvitableUsers,
        inviteMember,
        removeMember,
        canManageMembers,
        currentUser,
    } = useRag();

    const [selectedUserId, setSelectedUserId] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const currentBando = bandi.find((b) => b.id === currentBandoId);

    useEffect(() => {
        if (!open || !currentBandoId) return;
        loadMembers(currentBandoId);
        if (canManageMembers) {
            loadInvitableUsers(currentBandoId);
        }
    }, [open, currentBandoId, canManageMembers, loadMembers, loadInvitableUsers]);

    if (!open || !currentBandoId) return null;

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!selectedUserId) return;
        setSubmitting(true);
        try {
            await inviteMember(currentBandoId, parseInt(selectedUserId, 10));
            setSelectedUserId("");
        } catch {
            // error already in context
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemove = async (member) => {
        if (member.role === "owner") return;
        if (
            !window.confirm(
                `Rimuovere "${member.username || member.userId}" dal bando?`,
            )
        ) {
            return;
        }
        try {
            await removeMember(currentBandoId, member.userId);
        } catch {
            // error already in context
        }
    };

    return (
        <div
            className="rag-members-modal__backdrop"
            role="dialog"
            aria-modal="true"
            onClick={onClose}
        >
            <div
                className="rag-members-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="rag-members-modal__header">
                    <h3 className="rag-members-modal__title">
                        Membri del bando «{currentBando?.name ?? "—"}»
                    </h3>
                    <button
                        type="button"
                        className="rag-members-modal__close"
                        onClick={onClose}
                        aria-label="Chiudi"
                    >
                        <X size={16} />
                    </button>
                </header>

                <div className="rag-members-modal__body">
                    <section className="rag-members-modal__section">
                        <h4 className="rag-members-modal__section-title">
                            Membri attuali
                        </h4>
                        {loading.members ? (
                            <p className="rag-members-modal__loading">Caricamento…</p>
                        ) : members.length === 0 ? (
                            <p className="rag-members-modal__empty">Nessun membro.</p>
                        ) : (
                            <ul className="rag-members-modal__list">
                                {members.map((m) => {
                                    const isMe = currentUser?.id === m.userId;
                                    const removable = canManageMembers && m.role !== "owner";
                                    return (
                                        <li key={m.userId} className="rag-members-modal__item">
                                            <span className="rag-members-modal__user">
                                                {m.role === "owner" ? (
                                                    <Crown size={14} className="rag-members-modal__icon-owner" />
                                                ) : (
                                                    <User size={14} className="rag-members-modal__icon-member" />
                                                )}
                                                <span className="rag-members-modal__username">
                                                    {m.username || `#${m.userId}`}
                                                    {isMe && <span className="rag-members-modal__me"> (tu)</span>}
                                                </span>
                                                <span
                                                    className={`rag-members-modal__role rag-members-modal__role--${m.role}`}
                                                >
                                                    {m.role === "owner" ? "Owner" : "Membro"}
                                                </span>
                                            </span>
                                            {removable && (
                                                <button
                                                    type="button"
                                                    className="rag-members-modal__btn rag-members-modal__btn--danger"
                                                    onClick={() => handleRemove(m)}
                                                    aria-label={`Rimuovi ${m.username || m.userId}`}
                                                >
                                                    <UserMinus size={14} />
                                                    <span>Rimuovi</span>
                                                </button>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>

                    {canManageMembers && (
                        <section className="rag-members-modal__section">
                            <h4 className="rag-members-modal__section-title">
                                Invita un utente
                            </h4>
                            <form className="rag-members-modal__invite" onSubmit={handleInvite}>
                                <select
                                    className="rag-members-modal__select"
                                    value={selectedUserId}
                                    onChange={(e) => setSelectedUserId(e.target.value)}
                                    disabled={loading.invitableUsers || submitting}
                                >
                                    <option value="">— Seleziona utente —</option>
                                    {invitableUsers.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.username || `#${u.id}`}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="submit"
                                    className="rag-members-modal__btn rag-members-modal__btn--primary"
                                    disabled={!selectedUserId || submitting}
                                >
                                    <UserPlus size={14} />
                                    <span>{submitting ? "Invito…" : "Invita"}</span>
                                </button>
                            </form>
                            {!loading.invitableUsers && invitableUsers.length === 0 && (
                                <p className="rag-members-modal__empty">
                                    Tutti gli utenti registrati sono gia' membri.
                                </p>
                            )}
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}
